import { getAnalytics, isSupported as isAnalyticsSupported } from 'firebase/analytics';
import { initializeApp } from 'firebase/app';
import { User as AuthUser, connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { getMessaging, isSupported as isMessagingSupported } from 'firebase/messaging';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { config } from 'config/environments';

const firebaseApp = initializeApp(config.firebase);

const auth = getAuth(firebaseApp);

// When REACT_APP_FIREBASE_AUTH_EMULATOR_HOST is set (local dev only), route all
// auth through the Firebase Auth emulator instead of real Firebase.
const authEmulatorHost = process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST;
if (authEmulatorHost) {
  connectAuthEmulator(auth, `http://${authEmulatorHost}`, { disableWarnings: true });
}

const getFirebaseMessaging = async () => {
  if (await isMessagingSupported()) {
    return getMessaging(firebaseApp);
  }
  return null;
};

const getGoogleAnalytics = async () => {
  if (await isAnalyticsSupported()) {
    return getAnalytics(firebaseApp);
  }
  return null;
};

const qcStorage = getStorage(firebaseApp, config.qcStorageBucket);
const storage = getStorage(firebaseApp);

// When REACT_APP_FIREBASE_STORAGE_EMULATOR_HOST is set (local dev only), route
// file uploads through the Firebase Storage emulator instead of the real bucket
// (mirrors the auth emulator wiring above).
const storageEmulatorHost = process.env.REACT_APP_FIREBASE_STORAGE_EMULATOR_HOST;
if (storageEmulatorHost) {
  const [host, port] = storageEmulatorHost.split(':');
  connectStorageEmulator(storage, host, Number(port));
  connectStorageEmulator(qcStorage, host, Number(port));
}

const firestore = getFirestore(firebaseApp);

// Live print-relay status (collection `printJobs` in the `market` Native-mode database, kept
// separate from `(default)` so its ruleset exposes only printJobs) is read directly here. The
// relay writes it (Admin SDK); this reads it live — kept in sync with relay.py's
// FIRESTORE_DATABASE and the terraform google_firestore_database.
//
// During local dev the status lives in a DIFFERENT Firebase project than the app: the relay dev
// harness writes to the emulator under `nile-local`, while the app authenticates against
// nile-staging. So give print status its own app, pointed at that project, without disturbing
// auth/chat. When REACT_APP_FIREBASE_PRINT_PROJECT_ID is unset (every real environment) it falls
// back to the main app — so this is a no-op outside local dev.
const printProjectId = process.env.REACT_APP_FIREBASE_PRINT_PROJECT_ID;
const printApp = printProjectId
  ? initializeApp({ ...config.firebase, projectId: printProjectId }, 'print')
  : firebaseApp;
const printFirestore = getFirestore(printApp, 'market');

// When REACT_APP_FIREBASE_FIRESTORE_EMULATOR_HOST is set (local dev only), route ONLY the print
// Firestore through the emulator to read the relay dev harness's status writes (backend/relay/dev,
// Firestore emulator on :8086). Chat and everything else stay on real Firestore.
const firestoreEmulatorHost = process.env.REACT_APP_FIREBASE_FIRESTORE_EMULATOR_HOST;
if (firestoreEmulatorHost) {
  const [host, port] = firestoreEmulatorHost.split(':');
  connectFirestoreEmulator(printFirestore, host, Number(port));
}

export type User = AuthUser;
export { auth, firebaseApp, firestore, printFirestore, getFirebaseMessaging, getGoogleAnalytics, qcStorage, storage };
export default firebaseApp;
